import {
  COMPANY_ADDRESS_FULL,
  COMPANY_FACTS,
  COMPANY_LOCATION,
  COMPANY_NAME,
  MAP_URL,
  SERVICES,
} from './constants';

function Header() {
  const navItems = [
    { label: 'About', href: '#about' },
    { label: 'Services', href: '#services' },
    { label: 'Credentials', href: '#credentials' },
    { label: 'Contact', href: '#contact' },
  ];

  return (
    <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-4">
        <a href="#top" className="flex items-center gap-3 font-semibold text-ink">
          <span className="flex h-10 w-10 items-center justify-center rounded bg-route text-sm font-bold text-white">
            BB
          </span>
          <span className="tracking-wide">{COMPANY_NAME}</span>
        </a>
        <nav className="hidden items-center gap-6 text-sm font-medium text-slate-600 md:flex">
          {navItems.map((item) => (
            <a key={item.href} href={item.href} className="hover:text-route">
              {item.label}
            </a>
          ))}
        </nav>
        <a
          href="#credentials"
          className="rounded bg-signal px-4 py-2 text-sm font-semibold text-ink shadow-sm transition hover:bg-[#dba400]"
        >
          View Details
        </a>
      </div>
    </header>
  );
}

function FactCard({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-sm font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-3 text-2xl font-semibold text-ink">{value}</p>
    </div>
  );
}

function SectionHeading({ eyebrow, title, children }) {
  return (
    <div className="max-w-2xl">
      <p className="text-sm font-semibold uppercase tracking-[0.18em] text-route">{eyebrow}</p>
      <h2 className="mt-3 text-3xl font-bold text-ink sm:text-4xl">{title}</h2>
      {children ? <p className="mt-4 leading-7 text-slate-600">{children}</p> : null}
    </div>
  );
}

function App() {
  return (
    <div id="top" className="min-h-screen bg-slate-50 text-slate-700">
      <Header />

      <main>
        <section className="border-b border-slate-200 bg-white">
          <div className="mx-auto grid max-w-6xl gap-10 px-5 py-16 md:grid-cols-[1.08fr_0.92fr] md:items-center lg:py-24">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-route">
                Cincinnati Business Profile
              </p>
              <h1 className="mt-5 text-5xl font-black tracking-normal text-ink sm:text-6xl lg:text-7xl">
                {COMPANY_NAME}
              </h1>
              <p className="mt-6 max-w-2xl text-xl leading-8 text-slate-600">
                Business profile for a Cincinnati-based mobile bookstore and community book access organization.
              </p>
              <p className="mt-5 max-w-2xl leading-7 text-slate-600">
                This page organizes public-facing business information for readers, local partners, and community
                directories.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href="#credentials"
                  className="rounded bg-route px-5 py-3 text-center font-semibold text-white shadow-sm transition hover:bg-[#0b5875]"
                >
                  Company Details
                </a>
                <a
                  href="#contact"
                  className="rounded border border-slate-300 px-5 py-3 text-center font-semibold text-ink transition hover:border-route hover:text-route"
                >
                  Contact Location
                </a>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 shadow-soft">
              <div className="rounded bg-ink p-6 text-white">
                <p className="text-sm uppercase tracking-[0.18em] text-slate-300">Company Snapshot</p>
                <p className="mt-8 text-4xl font-bold">{COMPANY_NAME}</p>
                <div className="mt-8 space-y-4 text-sm">
                  <div className="flex justify-between gap-4 border-t border-white/15 pt-4">
                    <span className="text-slate-300">Location</span>
                    <span className="font-semibold">{COMPANY_LOCATION}</span>
                  </div>
                  <div className="border-t border-white/15 pt-4">
                    <span className="text-slate-300">Address</span>
                    <p className="mt-2 font-semibold">{COMPANY_ADDRESS_FULL}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section aria-label="Company facts" className="mx-auto grid max-w-6xl gap-4 px-5 py-10 md:grid-cols-3">
          {COMPANY_FACTS.map((fact) => (
            <FactCard key={fact.label} label={fact.label} value={fact.value} />
          ))}
        </section>

        <section id="about" className="mx-auto max-w-6xl px-5 py-14">
          <SectionHeading eyebrow="About" title="A focused company profile">
            BOOK BUS is listed at 3614 Section Rd in Cincinnati, Ohio. Public listings describe it as a mobile bookstore
            connected to books, education, and community access.
          </SectionHeading>
        </section>

        <section id="services" className="bg-white">
          <div className="mx-auto max-w-6xl px-5 py-16">
            <SectionHeading eyebrow="Services" title="Business overview">
              These details summarize public listings without exposing registry numbers or adding backend booking
              features.
            </SectionHeading>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {SERVICES.map((service) => (
                <article key={service.title} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="mb-5 h-2 w-12 rounded-full bg-signal" />
                  <h3 className="text-xl font-bold text-ink">{service.title}</h3>
                  <p className="mt-4 leading-7 text-slate-600">{service.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="credentials" className="mx-auto max-w-6xl px-5 py-16">
          <div className="grid gap-8 rounded-lg border border-slate-200 bg-white p-6 shadow-sm md:grid-cols-[0.85fr_1.15fr] md:p-10">
            <SectionHeading eyebrow="Credentials" title="Company identifiers">
              Public-facing profile details are shown clearly for directory, partner, and visitor reference.
            </SectionHeading>
            <dl className="grid gap-4">
              <div className="rounded bg-slate-50 p-5">
                <dt className="text-sm font-semibold uppercase tracking-wide text-slate-500">Company Location</dt>
                <dd className="mt-2 text-2xl font-bold text-ink">{COMPANY_LOCATION}</dd>
              </div>
              <div className="rounded bg-slate-50 p-5">
                <dt className="text-sm font-semibold uppercase tracking-wide text-slate-500">Company Address</dt>
                <dd className="mt-2 text-xl font-bold text-ink">{COMPANY_ADDRESS_FULL}</dd>
              </div>
            </dl>
          </div>
        </section>

        <section id="contact" className="bg-ink text-white">
          <div className="mx-auto grid max-w-6xl gap-8 px-5 py-16 md:grid-cols-[0.9fr_1.1fr] md:items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-signal">Contact Location</p>
              <h2 className="mt-3 text-3xl font-bold sm:text-4xl">Cincinnati, Ohio</h2>
              <p className="mt-4 leading-7 text-slate-300">
                Use the company address below for location reference. No phone number or email has been added because
                those details were not included in the provided source file.
              </p>
            </div>
            <div className="rounded-lg border border-white/10 bg-white/5 p-6">
              <p className="text-sm font-semibold uppercase tracking-wide text-slate-300">Company Address</p>
              <p className="mt-3 text-2xl font-bold">{COMPANY_ADDRESS_FULL}</p>
              <a
                href={MAP_URL}
                target="_blank"
                rel="noreferrer"
                className="mt-6 inline-flex rounded bg-signal px-5 py-3 font-semibold text-ink transition hover:bg-[#dba400]"
              >
                Open Map
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-5 py-8 text-sm text-slate-500 md:flex-row md:items-center md:justify-between">
          <p>
            &copy; {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.
          </p>
          <p>{COMPANY_LOCATION}</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
